<script setup lang="ts">
import {
  ArrowRight,
  BriefcaseBusiness,
  Languages,
  MapPin,
  MessageCircle,
  Star,
  UserRound,
} from "lucide-vue-next";
import type { Worker } from "~/services/search";
import { toTitleCase } from "~/utils/search-catalog";
import { formatPersonName, getWorkerProfilePath } from "~/utils/worker-profile";

const props = defineProps<{
  worker: Worker;
  selectedServiceName?: string;
  selectedCategoryName?: string;
  selectedLanguageCodes?: string[];
  regionName?: string;
}>();

const router = useRouter();
const localeRoute = useLocaleRoute();
const { t } = useI18n();
const isProfileImageBroken = ref(false);
const brokenGalleryUrls = ref<string[]>([]);
const publicProfilePath = computed(() => getWorkerProfilePath(props.worker));
const profileRoute = computed(() => localeRoute(publicProfilePath.value)!);
const profileContactRoute = computed(
  () => localeRoute({ path: publicProfilePath.value, hash: "#contacts" })!,
);
const profileServicesRoute = computed(
  () => localeRoute({ path: publicProfilePath.value, hash: "#portfolio" })!,
);
const workerName = computed(() =>
  formatPersonName(props.worker.name, props.worker.lastname),
);

function uniqueValues(values: Array<string | null | undefined>) {
  const seen = new Set<string>();

  return values.flatMap(value => {
    const normalized = value?.trim();
    if (!normalized) return [];

    const key = normalized.toLocaleLowerCase();
    if (seen.has(key)) return [];

    seen.add(key);
    return [normalized];
  });
}

function catalogLabel(
  type: "services" | "category" | "region" | "language" | "search.card.pricing",
  value: string,
) {
  const key = `${type}.${value}`;
  const translated = t(key);
  return translated === key ? toTitleCase(value) : translated;
}

const regionInfo = computed(() => {
  const selectedRegion = props.regionName?.trim();

  if (selectedRegion) {
    return {
      label: catalogLabel("region", selectedRegion),
      additionalCount: 0,
      hiddenLabels: "",
    };
  }

  if (props.worker.coverageType === "nationwide") {
    return {
      label: t("search.card.nationwide"),
      additionalCount: 0,
      hiddenLabels: "",
    };
  }

  const [firstRegion, ...otherRegions] = uniqueValues([
    ...props.worker.regions,
    props.worker.primaryRegionName,
  ]);

  return {
    label: firstRegion ? catalogLabel("region", firstRegion) : "",
    additionalCount: otherRegions.length,
    hiddenLabels: otherRegions
      .map(region => catalogLabel("region", region))
      .join(", "),
  };
});

const languageInfo = computed(() => {
  const workerLanguages = uniqueValues(props.worker.languages);
  const workerLanguageMap = new Map(
    workerLanguages.map(code => [code.toLocaleLowerCase(), code]),
  );
  const displayedLanguages = props.selectedLanguageCodes?.length
    ? props.selectedLanguageCodes.flatMap(code => {
        const match = workerLanguageMap.get(code.toLocaleLowerCase());
        return match ? [match] : [];
      })
    : workerLanguages;
  const [firstLanguage, ...otherLanguages] = displayedLanguages;

  return {
    label: firstLanguage ? catalogLabel("language", firstLanguage) : "",
    additionalCount: otherLanguages.length,
    hiddenLabels: otherLanguages
      .map(code => catalogLabel("language", code))
      .join(", "),
  };
});

function numericPrice(service: Worker["services"][number]) {
  if (service.price.type === "negotiable") return null;
  return service.price.from ?? service.price.to ?? null;
}

const selectedService = computed(() =>
  props.selectedServiceName
    ? props.worker.services.find(
        service => service.name === props.selectedServiceName,
      )
    : undefined,
);
const selectedServicePrice = computed(() =>
  selectedService.value ? priceValue(selectedService.value) : "",
);

const orderedServices = computed(() => {
  const services = [...props.worker.services];
  const category = props.selectedCategoryName;

  return services.sort((first, second) => {
    if (first.id === selectedService.value?.id) return -1;
    if (second.id === selectedService.value?.id) return 1;

    if (category) {
      const firstMatchesCategory = first.categoryName === category;
      const secondMatchesCategory = second.categoryName === category;
      if (firstMatchesCategory !== secondMatchesCategory) {
        return firstMatchesCategory ? -1 : 1;
      }
    }

    const firstHasPrice = numericPrice(first) != null;
    const secondHasPrice = numericPrice(second) != null;
    if (firstHasPrice !== secondHasPrice) return firstHasPrice ? -1 : 1;

    return first.id - second.id;
  });
});

const displayedServices = computed(() => orderedServices.value.slice(0, 4));
const otherServiceCount = computed(() =>
  selectedService.value ? Math.max(0, orderedServices.value.length - 1) : 0,
);
const remainingServiceCount = computed(() =>
  Math.max(0, props.worker.services.length - displayedServices.value.length),
);
const primaryCategoryName = computed(
  () =>
    selectedService.value?.categoryName ||
    props.selectedCategoryName ||
    props.worker.primaryCategoryName ||
    props.worker.categories[0] ||
    "",
);
const overview = computed(() => props.worker.overview?.trim() || "");
const isLongDescription = computed(() => overview.value.length > 135);

const galleryPhotos = computed(() => {
  const broken = new Set(brokenGalleryUrls.value);
  const seen = new Set<string>();
  return orderedServices.value.flatMap(service =>
    service.portfolioItems.flatMap(item => {
      const url = item.photoURL?.trim();
      if (!url || broken.has(url) || seen.has(url)) return [];
      seen.add(url);
      return [{ url, serviceName: service.name }];
    }),
  );
});
const visibleGalleryPhotos = computed(() => galleryPhotos.value.slice(0, 4));
const galleryOverflowCount = computed(() =>
  Math.max(0, galleryPhotos.value.length - 4),
);
const mobileGalleryOverflowCount = computed(() =>
  Math.max(0, galleryPhotos.value.length - 2),
);

const ratingLabel = computed(() =>
  props.worker.avgRating ? props.worker.avgRating.toFixed(1) : "—",
);

function markGalleryBroken(url: string) {
  if (!brokenGalleryUrls.value.includes(url)) {
    brokenGalleryUrls.value = [...brokenGalleryUrls.value, url];
  }
}

function pricingUnitSuffix(unit?: string) {
  if (unit === "hour")
    return ` / ${t("worker_profile_page.services.pricing.unit_hour_short")}`;
  if (unit === "m2")
    return ` / ${t("worker_profile_page.services.pricing.unit_m2_short")}`;
  if (unit === "meter")
    return ` / ${t("worker_profile_page.services.pricing.unit_meter_short")}`;
  if (unit === "unit")
    return ` / ${t("worker_profile_page.services.pricing.unit_unit_short")}`;
  return "";
}

function priceValue(service: Worker["services"][number]) {
  const { from, to, type, unit } = service.price;
  const suffix = pricingUnitSuffix(unit);

  if (type === "negotiable") {
    return catalogLabel("search.card.pricing", "negotiable");
  }
  if (type === "range") {
    if (from == null && to == null) {
      return catalogLabel("search.card.pricing", "negotiable");
    }
    if (from != null && to != null) return `${from}–${to} €${suffix}`;
    return `${from ?? to} €${suffix}`;
  }
  if (from == null) {
    return catalogLabel("search.card.pricing", "negotiable");
  }
  if (type === "from")
    return `${t("search.card.from_price")} ${from} €${suffix}`;
  return `${from} €${suffix}`;
}
</script>

<template>
  <article
    class="group cursor-pointer overflow-hidden rounded-[18px] border border-[#E1E8E4] bg-white transition duration-200 hover:border-primary/30 hover:shadow-[0_10px_28px_rgba(17,30,25,0.055)]"
    role="link"
    tabindex="0"
    @click="router.push(profileRoute)"
    @keydown.enter.prevent="router.push(profileRoute)"
    @keydown.space.prevent="router.push(profileRoute)"
  >
    <div
      class="grid min-w-0 gap-0 md:grid-cols-[330px_minmax(0,1fr)] min-[1180px]:h-[400px] min-[1180px]:grid-cols-[390px_minmax(0,1fr)_220px] min-[1440px]:grid-cols-[410px_minmax(0,1fr)_225px]"
    >
      <!-- Portfolio / media -->
      <div
        class="relative min-h-[230px] border-b border-[#E6ECE8] bg-[#F8FAF9] md:border-b-0 md:border-r"
      >
        <template v-if="visibleGalleryPhotos.length">
          <!-- Mobile keeps the visual compact: one hero + one overflow tile. -->
          <div
            class="grid h-[205px] gap-1 sm:hidden"
            :class="
              visibleGalleryPhotos.length > 1
                ? 'grid-cols-[minmax(0,2.4fr)_minmax(88px,1fr)]'
                : 'grid-cols-1'
            "
          >
            <div class="relative min-h-0 overflow-hidden bg-[#EEF3F0]">
              <img
                :src="visibleGalleryPhotos[0].url"
                :alt="
                  catalogLabel('services', visibleGalleryPhotos[0].serviceName)
                "
                class="size-full object-cover"
                draggable="false"
                @error="markGalleryBroken(visibleGalleryPhotos[0].url)"
              />
            </div>
            <div
              v-if="visibleGalleryPhotos[1]"
              class="relative min-h-0 overflow-hidden bg-[#EEF3F0]"
            >
              <img
                :src="visibleGalleryPhotos[1].url"
                :alt="
                  catalogLabel('services', visibleGalleryPhotos[1].serviceName)
                "
                class="size-full object-cover"
                draggable="false"
                @error="markGalleryBroken(visibleGalleryPhotos[1].url)"
              />
              <div
                v-if="mobileGalleryOverflowCount"
                class="absolute inset-0 flex items-center justify-center bg-black/50 text-xl font-bold text-white backdrop-blur-[1px]"
              >
                +{{ mobileGalleryOverflowCount }}
              </div>
            </div>
          </div>

          <!-- Tablet/desktop use the full one-large + three-right collage. -->
          <div
            class="hidden h-[260px] gap-1 sm:grid md:h-full md:min-h-[300px]"
            :class="{
              'grid-cols-1': visibleGalleryPhotos.length === 1,
              'grid-cols-[minmax(0,2.35fr)_minmax(92px,1fr)]':
                visibleGalleryPhotos.length > 1,
            }"
          >
            <div class="relative min-h-0 overflow-hidden bg-[#EEF3F0]">
              <img
                :src="visibleGalleryPhotos[0].url"
                :alt="
                  catalogLabel('services', visibleGalleryPhotos[0].serviceName)
                "
                class="size-full object-cover transition duration-500 group-hover:scale-[1.015]"
                draggable="false"
                @error="markGalleryBroken(visibleGalleryPhotos[0].url)"
              />
            </div>

            <div
              v-if="visibleGalleryPhotos.length > 1"
              class="grid min-h-0 gap-1.5"
              :class="{
                'grid-rows-1': visibleGalleryPhotos.length === 2,
                'grid-rows-2': visibleGalleryPhotos.length === 3,
                'grid-rows-3': visibleGalleryPhotos.length >= 4,
              }"
            >
              <div
                v-for="(photo, index) in visibleGalleryPhotos.slice(1)"
                :key="photo.url"
                class="relative min-h-0 overflow-hidden bg-[#EEF3F0]"
              >
                <img
                  :src="photo.url"
                  :alt="catalogLabel('services', photo.serviceName)"
                  class="size-full object-cover"
                  draggable="false"
                  @error="markGalleryBroken(photo.url)"
                />
                <div
                  v-if="index === 2 && galleryOverflowCount"
                  class="absolute inset-0 flex items-center justify-center bg-black/55 text-xl font-bold text-white backdrop-blur-[1px]"
                >
                  +{{ galleryOverflowCount }}
                </div>
              </div>
            </div>
          </div>
        </template>

        <div
          v-else
          class="relative flex h-[230px] items-center justify-center overflow-hidden bg-[#FBFCFB] px-6 sm:h-[260px] md:h-full md:min-h-[300px]"
        >
          <img
            src="/images/search/portfolio-empty-state.webp"
            alt=""
            class="absolute inset-0 size-full scale-[1.03] object-cover object-center opacity-[0.32]"
            loading="lazy"
            draggable="false"
          />
          <div class="relative z-10 max-w-[235px] text-center">
            <div
              class="font-gabarito text-lg font-bold tracking-[-0.01em] text-app-text"
            >
              {{ t("search.card.empty_portfolio_short") }}
            </div>
            <p
              class="mx-auto mt-3 max-w-[225px] text-xs leading-5 text-app-muted"
            >
              {{ t("search.card.empty_portfolio_description") }}
            </p>
          </div>
        </div>

        <div
          class="absolute bottom-4 left-4 z-10 flex size-[76px] items-center justify-center overflow-hidden rounded-full border-[4px] border-white bg-white text-xl font-bold text-primary shadow-[0_7px_22px_rgba(16,36,29,0.14)] sm:size-[80px]"
        >
          <img
            v-if="worker.profileImageURL && !isProfileImageBroken"
            :src="worker.profileImageURL"
            :alt="workerName"
            class="size-full object-cover"
            draggable="false"
            @error="isProfileImageBroken = true"
          />
          <UserRound
            v-else
            class="size-8 text-primary"
            :stroke-width="1.6"
            aria-hidden="true"
          />
        </div>
      </div>

      <!-- Profile + services -->
      <div class="relative flex min-w-0 flex-col p-4 sm:p-5 lg:p-5 xl:px-6">
        <div class="flex min-w-0 items-start justify-between gap-4">
          <div class="min-w-0">
            <NuxtLink
              :to="profileRoute"
              class="line-clamp-1 font-gabarito text-xl font-bold leading-7 tracking-[-0.015em] text-app-text transition hover:text-primary"
              @click.stop
            >
              {{ workerName }}
            </NuxtLink>
            <div class="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
              <span
                class="inline-flex items-center gap-1.5 rounded-full bg-[#F4F8F6] px-2.5 py-1 font-semibold text-primary"
              >
                <Star class="size-3.5 fill-current" :stroke-width="1.8" />
                {{ ratingLabel }}
                <span class="font-medium text-app-muted"
                  >({{ worker.reviewCount }})</span
                >
              </span>
              <span
                v-if="primaryCategoryName"
                class="inline-flex items-center rounded-full bg-[#F7F8F7] px-2.5 py-1 font-semibold text-app-text"
              >
                {{ catalogLabel("category", primaryCategoryName) }}
              </span>
            </div>
          </div>
        </div>

        <div class="mt-3">
          <p
            v-if="overview"
            class="line-clamp-2 text-sm leading-[1.55] text-app-muted"
          >
            {{ overview }}
          </p>
          <p v-else class="text-sm leading-[1.55] text-app-muted/75">
            {{ t("search.card.no_description") }}
          </p>
          <NuxtLink
            v-if="isLongDescription"
            :to="profileRoute"
            class="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-primary transition duration-300 hover:text-primary-700"
            @click.stop
          >
            {{ t("search.card.read_more") }}
            <ArrowRight class="size-3" />
          </NuxtLink>
        </div>

        <template v-if="selectedService">
          <div class="relative mt-5">
            <div
              class="absolute left-4 top-0 z-10 -translate-y-1/2 rounded-full bg-primary px-2.5 py-0.5 text-xs font-bold leading-none text-white shadow-[0_2px_8px_rgba(19,122,104,0.16)]"
            >
              {{ t("search.card.search_match") }}
            </div>

            <div
              class="flex min-h-[78px] items-center rounded-[11px] border border-primary bg-white px-4 pb-3 pt-4 shadow-[0_5px_18px_rgba(19,122,104,0.07)]"
            >
              <div
                class="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-4"
              >
                <div
                  class="flex min-h-6 min-w-0 items-center text-sm font-semibold leading-6 text-app-text"
                >
                  {{ catalogLabel("services", selectedService.name) }}
                </div>
                <div
                  v-if="selectedServicePrice"
                  class="flex min-h-6 shrink-0 items-center whitespace-nowrap text-lg font-bold leading-6 text-primary"
                >
                  {{ selectedServicePrice }}
                </div>
              </div>
            </div>
          </div>

          <div v-if="otherServiceCount" class="mt-2" @click.stop>
            <NuxtLink
              :to="profileServicesRoute"
              class="inline-flex h-8 items-center gap-1.5 rounded-lg px-1 text-xs font-semibold text-primary transition duration-300 hover:text-primary-700"
            >
              {{
                t("search.card.show_other_services", {
                  count: otherServiceCount,
                })
              }}
              <ArrowRight class="size-3.5" />
            </NuxtLink>
          </div>
        </template>

        <template v-else>
          <div
            class="mt-4 grid gap-2"
            :class="
              displayedServices.length === 1 ? 'grid-cols-1' : 'grid-cols-2'
            "
          >
            <div
              v-for="service in displayedServices"
              :key="service.id"
              class="relative flex h-[78px] min-w-0 flex-col justify-center gap-1.5 rounded-[10px] border border-[#E1E8E4] bg-white px-3.5 py-2.5 transition"
            >
              <div
                class="line-clamp-2 text-sm font-semibold leading-[1.3] text-app-text"
              >
                {{ catalogLabel("services", service.name) }}
              </div>
              <div
                class="max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-xs font-medium leading-4 text-app-muted"
              >
                {{ priceValue(service) }}
              </div>
            </div>

            <NuxtLink
              v-if="remainingServiceCount"
              :to="profileServicesRoute"
              class="col-span-2 flex h-7 items-center justify-end gap-1.5 text-xs font-semibold text-primary transition duration-300 hover:text-primary-700 sm:hidden"
              @click.stop
            >
              {{
                t("search.card.see_more_services", {
                  count: remainingServiceCount,
                })
              }}
              <ArrowRight class="size-3.5" />
            </NuxtLink>
          </div>

          <div
            v-if="remainingServiceCount"
            class="mt-2 hidden items-center justify-end sm:flex"
            @click.stop
          >
            <NuxtLink
              :to="profileServicesRoute"
              class="inline-flex items-center gap-1.5 text-xs font-semibold text-primary transition duration-300 hover:text-primary-700"
            >
              {{
                t("search.card.see_more_services", {
                  count: remainingServiceCount,
                })
              }}
              <ArrowRight class="size-3.5" />
            </NuxtLink>
          </div>
        </template>
      </div>

      <!-- CTA + quick facts -->
      <aside
        class="flex flex-col border-t border-[#E6ECE8] p-4 sm:p-5 md:col-span-2 min-[1180px]:col-span-1 min-[1180px]:border-l min-[1180px]:border-t-0"
      >
        <div
          class="order-2 mt-5 grid grid-cols-2 gap-2 min-[1180px]:order-1 min-[1180px]:mt-0 min-[1180px]:grid-cols-1"
          @click.stop
        >
          <NuxtLink
            :to="profileRoute"
            class="flex h-11 items-center justify-center gap-2 rounded-[9px] bg-primary px-3 text-sm font-semibold text-white transition duration-300 hover:bg-primary-400"
          >
            <UserRound class="size-4" :stroke-width="1.9" />
            {{ t("search.card.view_profile") }}
          </NuxtLink>
          <NuxtLink
            :to="profileContactRoute"
            class="flex h-11 items-center justify-center gap-2 rounded-[9px] border border-primary/60 bg-white px-3 text-sm font-semibold text-primary transition duration-300 hover:border-primary hover:bg-primary-50"
          >
            <MessageCircle class="size-4" :stroke-width="1.9" />
            {{ t("search.card.message") }}
          </NuxtLink>
        </div>

        <dl
          class="order-1 grid grid-cols-2 gap-x-4 gap-y-4 text-xs md:grid-cols-4 min-[1180px]:order-2 min-[1180px]:mt-5 min-[1180px]:grid-cols-1 min-[1180px]:gap-y-4"
        >
          <div class="flex items-start gap-3">
            <BriefcaseBusiness
              class="mt-0.5 size-[17px] shrink-0 text-primary"
              :stroke-width="1.7"
            />
            <div>
              <dt class="text-xs text-app-muted">
                {{ t("search.card.experience") }}
              </dt>
              <dd class="mt-0.5 font-semibold text-app-text">
                {{
                  worker.experienceYears > 0
                    ? t("search.card.experience_years_display", {
                        count: Math.ceil(worker.experienceYears),
                      })
                    : "—"
                }}
              </dd>
            </div>
          </div>

          <div v-if="regionInfo.label" class="flex items-start gap-3">
            <MapPin class="mt-0.5 size-4 shrink-0 text-primary" />
            <div class="min-w-0">
              <dt class="text-xs text-app-muted">
                {{ t("search.card.service_area") }}
              </dt>
              <dd
                class="mt-0.5 flex min-w-0 items-center gap-1 font-semibold text-app-text"
              >
                <span class="truncate">{{ regionInfo.label }}</span>
                <AppTooltip
                  v-if="regionInfo.additionalCount"
                  :text="regionInfo.hiddenLabels"
                  placement="top"
                  align="end"
                >
                  <span
                    class="shrink-0 cursor-help rounded-full bg-primary-50 px-1.5 py-0.5 text-xs font-bold text-primary"
                  >
                    +{{ regionInfo.additionalCount }}
                  </span>
                </AppTooltip>
              </dd>
            </div>
          </div>

          <div v-if="languageInfo.label" class="flex items-start gap-3">
            <Languages
              class="mt-0.5 size-[17px] shrink-0 text-primary"
              :stroke-width="1.7"
            />
            <div class="min-w-0">
              <dt class="text-xs text-app-muted">
                {{ t("search.card.languages") }}
              </dt>
              <dd
                class="mt-0.5 flex min-w-0 items-center gap-1 font-semibold text-app-text"
              >
                <span class="truncate">{{ languageInfo.label }}</span>
                <AppTooltip
                  v-if="languageInfo.additionalCount"
                  :text="languageInfo.hiddenLabels"
                  placement="top"
                  align="end"
                >
                  <span
                    class="shrink-0 cursor-help rounded-full bg-primary-50 px-1.5 py-0.5 text-xs font-bold text-primary"
                  >
                    +{{ languageInfo.additionalCount }}
                  </span>
                </AppTooltip>
              </dd>
            </div>
          </div>

          <div class="flex items-start gap-3">
            <svg
              class="mt-0.5 size-4 shrink-0 text-primary"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <circle
                cx="4"
                cy="4"
                r="2.1"
                stroke="currentColor"
                stroke-width="1.35"
              />
              <circle
                cx="12"
                cy="4"
                r="2.1"
                stroke="currentColor"
                stroke-width="1.35"
              />
              <circle
                cx="4"
                cy="12"
                r="2.1"
                stroke="currentColor"
                stroke-width="1.35"
              />
              <circle
                cx="12"
                cy="12"
                r="2.1"
                stroke="currentColor"
                stroke-width="1.35"
              />
            </svg>
            <div>
              <dt class="text-xs text-app-muted">
                {{ t("search.card.services") }}
              </dt>
              <dd class="mt-0.5 font-semibold text-app-text">
                {{
                  t("search.card.service_count", {
                    count: worker.services.length,
                  })
                }}
              </dd>
            </div>
          </div>
        </dl>
      </aside>
    </div>
  </article>
</template>
